import { useState } from 'react'
import { Upload, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet, X } from 'lucide-react'
import { parseKycWorkbook } from '../kycImportParser'
import type { KycImportResult, SheetValidation } from '../kycImportParser'
import { coverageApi } from '@/api/client'
import { showToast } from '@/store/uiStore'

type Stage = 'idle' | 'parsing' | 'preview' | 'importing' | 'done'

interface ImportSummary {
  countries: number
  sources: number
  recommendations: number
}

function ValidationRow({ v }: { v: SheetValidation }) {
  const allOk = v.found && v.headersMissing.length === 0
  return (
    <tr className="border-b border-[var(--border-color)]">
      <td className="py-2 px-3 text-sm text-[var(--text-primary)] font-medium">{v.sheetName}</td>
      <td className="py-2 px-3 text-sm text-center">
        {v.found ? (
          <span className="text-green-400">{v.rowCount}</span>
        ) : (
          <span className="text-red-400">Not found</span>
        )}
      </td>
      <td className="py-2 px-3 text-center">
        {allOk ? (
          <CheckCircle2 size={16} className="text-green-400 inline" />
        ) : v.found ? (
          <AlertTriangle size={16} className="text-amber-400 inline" />
        ) : (
          <X size={16} className="text-red-400 inline" />
        )}
      </td>
      <td className="py-2 px-3 text-xs text-[var(--text-muted)]">
        {v.headersMissing.length > 0 && (
          <span className="text-amber-400">Missing: {v.headersMissing.join(', ')}</span>
        )}
      </td>
    </tr>
  )
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (summary: ImportSummary) => void
}

export function KycImportModal({ isOpen, onClose, onSuccess }: Props) {
  const [stage, setStage] = useState<Stage>('idle')
  const [result, setResult] = useState<KycImportResult | null>(null)
  const [importProgress, setImportProgress] = useState('')
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setStage('idle')
    setResult(null)
    setImportProgress('')
    setImportSummary(null)
    setError(null)
  }

  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showToast('Please select an .xlsx file', 'error')
      return
    }

    setStage('parsing')
    setError(null)

    try {
      const parsed = await parseKycWorkbook(file)
      setResult(parsed)
      setStage('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file')
      setStage('idle')
    }
  }

  const handleImport = async () => {
    if (!result) return
    setStage('importing')
    setError(null)

    try {
      // 1. Countries
      setImportProgress('Importing countries (1/3)...')
      const r1 = await coverageApi.importCountries({
        source_file: result.sourceFileName,
        countries: result.countries,
      })
      if (!r1.success) throw new Error(r1.error || 'Country import failed')

      // 2. Sources
      setImportProgress('Importing data sources (2/3)...')
      const r2 = await coverageApi.importSources({
        source_file: result.sourceFileName,
        sources: result.sources,
      })
      if (!r2.success) throw new Error(r2.error || 'Source import failed')

      // 3. Recommendations
      setImportProgress('Importing recommendations (3/3)...')
      const r3 = await coverageApi.importRecommendations({
        source_file: result.sourceFileName,
        recommendations: result.recommendations,
      })
      if (!r3.success) throw new Error(r3.error || 'Recommendation import failed')

      const summary: ImportSummary = {
        countries: result.countries.length,
        sources: result.sources.length,
        recommendations: result.recommendations.length,
      }
      setImportSummary(summary)
      setStage('done')
      showToast('Import completed successfully', 'success')
      onSuccess?.(summary)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
      setStage('preview')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { if (stage !== 'importing') { reset(); onClose() } }}>
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Import Country Coverage</h3>
          {stage !== 'importing' && (
            <button onClick={() => { reset(); onClose() }} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="px-6 py-5">
          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400 flex items-start gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Stage: Idle — file drop */}
          {stage === 'idle' && (
            <div
              className="border-2 border-dashed border-[var(--border-color)] rounded-lg p-10 text-center hover:border-teal-500/50 transition-colors cursor-pointer"
              onDragOver={e => { e.preventDefault(); e.stopPropagation() }}
              onDrop={e => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
              onClick={() => document.getElementById('kyc-import-input')?.click()}
            >
              <input
                id="kyc-import-input"
                type="file"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
              />
              <Upload size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
              <p className="text-sm text-[var(--text-primary)] font-medium">
                Drag & drop the Country Coverage spreadsheet
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Expects: FrankieOne Country Coverage .xlsx with SUMMARY, KYC Summary, KYC, and KYC Recommendations sheets
              </p>
            </div>
          )}

          {/* Stage: Parsing */}
          {stage === 'parsing' && (
            <div className="text-center py-10">
              <Loader2 size={24} className="animate-spin mx-auto mb-3 text-teal-400" />
              <p className="text-sm text-[var(--text-primary)]">Reading spreadsheet...</p>
            </div>
          )}

          {/* Stage: Preview */}
          {stage === 'preview' && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                <FileSpreadsheet size={16} className="text-teal-400" />
                <span className="font-medium">{result.sourceFileName}</span>
              </div>

              {/* Validation table */}
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] text-[var(--text-muted)] uppercase border-b border-[var(--border-color)]">
                    <th className="text-left py-2 px-3">Sheet</th>
                    <th className="text-center py-2 px-3">Rows</th>
                    <th className="text-center py-2 px-3">Headers</th>
                    <th className="text-left py-2 px-3">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {result.validation.map(v => <ValidationRow key={v.sheetName} v={v} />)}
                </tbody>
              </table>

              {/* Summary counts */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-[var(--bg-secondary)] text-center">
                  <p className="text-xl font-bold text-teal-400">{result.countries.length}</p>
                  <p className="text-xs text-[var(--text-muted)]">Countries</p>
                </div>
                <div className="p-3 rounded-lg bg-[var(--bg-secondary)] text-center">
                  <p className="text-xl font-bold text-teal-400">{result.sources.length}</p>
                  <p className="text-xs text-[var(--text-muted)]">Data Sources</p>
                </div>
                <div className="p-3 rounded-lg bg-[var(--bg-secondary)] text-center">
                  <p className="text-xl font-bold text-teal-400">{result.recommendations.length}</p>
                  <p className="text-xs text-[var(--text-muted)]">Recommendations</p>
                </div>
              </div>

              <p className="text-xs text-[var(--text-muted)]">
                This will replace all existing coverage data. The import cannot be undone.
              </p>
            </div>
          )}

          {/* Stage: Importing */}
          {stage === 'importing' && (
            <div className="text-center py-10">
              <Loader2 size={24} className="animate-spin mx-auto mb-3 text-teal-400" />
              <p className="text-sm text-[var(--text-primary)]">{importProgress}</p>
            </div>
          )}

          {/* Stage: Done */}
          {stage === 'done' && importSummary && (
            <div className="text-center py-6">
              <CheckCircle2 size={32} className="mx-auto mb-3 text-green-400" />
              <p className="text-sm font-medium text-[var(--text-primary)]">Import Complete</p>
              <p className="text-xs text-[var(--text-muted)] mt-2">
                {importSummary.countries} countries, {importSummary.sources} data sources, {importSummary.recommendations} recommendations
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end gap-3">
          {stage === 'preview' && (
            <>
              <button
                onClick={() => { reset() }}
                className="px-4 py-2 text-sm rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 transition-colors"
              >
                Confirm Import
              </button>
            </>
          )}
          {stage === 'done' && (
            <button
              onClick={() => { reset(); onClose() }}
              className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
